import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { IAppState } from 'src/store';
import { Box, Button, Typography } from '@mui/material';

import colors from 'src/styles/equalution/colors';
import BoostImg from 'src/assets/images/boost.png';
import GoalsImg from 'src/assets/images/goals.png';
import SleepImg from 'src/assets/images/sleep.png';
import GoalPaceImage from 'src/assets/images/goal-pace.png';
import { goalDetails, motivationDetails } from 'src/pages/constants';
import { useHistory } from 'react-router-dom';
import { useQuizTracking } from '../../hooks';
import { EVENTS, trackQuizFunnel } from 'src/util/track';
import ClientTestimonials from './ClientTestimonials';

interface PlanObj {
  id: number;
  name: string;
  description: string;
  icon: string;
}

const PlanSummary = () => {
  const { quizData } = useSelector((state: IAppState) => state.quiz);
  const [programsList, setProgramsList] = React.useState<PlanObj[]>([]);
  const history = useHistory();

  const trackEvent = useQuizTracking();

  useEffect(() => {
    createProgramsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    trackQuizFunnel(EVENTS.quizResultsViewed, {
      quiz_type: 'equalution',
      country: quizData.located,
      email: quizData.email,
    });
  }, [quizData.email, quizData.located]);

  const handleNavigate = () => {
    let planName = 'standard';
    let planDuration = 3;
    if (quizData.weight && quizData.target_weight) {
      if (quizData.weight - quizData.target_weight >= 7) {
        planDuration = 6;
      } else if (quizData.weight - quizData.target_weight >= 2) {
        planDuration = 3;
      } else {
        planDuration = 1;
      }
    }

    if (quizData.located === 'Australia') {
      planName = 'standard';
    }
    if (quizData.problem_hitting_goal === 1) {
      planName = 'standard';
    }
    if (quizData.located === 'New Zealand') {
      planName = 'standard';
    }
    trackEvent('StartTransformation', {
      program: planName,
      duration: planDuration,
      email: quizData.email,
      country: quizData.located,
    });
    trackQuizFunnel(EVENTS.quizResultsCtaClicked, {
      quiz_type: 'equalution',
      recommended_plan_type: planName,
      recommended_plan_duration: planDuration,
      country: quizData.located,
    });

    const params = {
      planType: planName,
      duration: planDuration,
      country: quizData.located,
      email: quizData.email,
      firstName: quizData.FName,
      lastName: quizData.LName,
    };

    const queryString = Object.entries(params)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    const url = `/signup?${queryString}`;

    history.replace(url);
  };
  const createProgramsList = () => {
    const programsArr: PlanObj[] = [];
    let index = 0;
    //goal program as first item
    goalDetails.forEach((item: PlanObj) => {
      if (item.id === quizData.goal_id) {
        programsArr.push({ ...item, id: index });
        index++;
      }
    });
    quizData.goal_adherence_challenges?.forEach(goalVal => {
      motivationDetails.forEach(item => {
        if (item.id === goalVal) {
          programsArr.push({ ...item, id: index });
          index++;
        }
      });
    });
    setProgramsList(programsArr);
  };

  const GoalSelectionCard = ({
    title,
    description,
    icon,
  }: {
    title: string;
    description: string;
    icon: string;
  }) => {
    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            marginBlock: '12px',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              height={24}
              width={24}
              style={{ marginRight: '8px' }}
              src={icon}
              alt="icon"
            />
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: 600,
                color: colors.OFF_BLACK,
              }}>
              {title}
            </Typography>
          </Box>
          <Box
            sx={{
              borderBottom: `1px solid ${colors.SALAD}33`,
              width: '100%',
              marginBlock: '8px',
            }}
          />
          <Typography
            sx={{
              fontSize: '14px',
              color: colors.OFF_BLACK,
              opacity: 0.7,
            }}>
            {description}
          </Typography>
        </Box>
      </Box>
    );
  };

  const GoalSelectionSection = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          marginBlock: '20px',
          backgroundColor: colors.CREAM,
          borderRadius: '20px',
          padding: '16px',
        }}>
        {programsList.map(program => (
          <GoalSelectionCard
            key={program.id}
            title={program.name}
            description={program.description}
            icon={program.icon}
          />
        ))}
      </Box>
    );
  };

  const renderGoalSelection = () => {
    return <GoalSelectionSection />;
  };

  return (
    <Box sx={{ maxWidth: '420px' }}>
      {quizData?.FName && (
        <Typography sx={{ fontSize: '26px', fontWeight: 600 }}>
          {quizData.FName}'s Program
        </Typography>
      )}
      <Typography
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          marginBottom: '15px',
          marginTop: '20px',
        }}>
        What success looks like
      </Typography>
      {renderGoalSelection()}
      <Button
        variant="contained"
        onClick={handleNavigate}
        size="large"
        sx={{
          textTransform: 'none',
          borderRadius: '100px',
          marginTop: 4,
          width: '100%',
          height: '55px',
          alignSelf: 'center',
          marginBottom: 2,
        }}>
        Start my transformation!
      </Button>
      <Box
        component="img"
        src={GoalPaceImage}
        alt="goal-pace"
        height={'420px'}
        width={'330px'}
        sx={{
          marginBottom: '8px',
          marginTop: '30px',
          display: 'block',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      />
      <Button
        variant="contained"
        onClick={handleNavigate}
        size="large"
        sx={{
          textTransform: 'none',
          borderRadius: '100px',
          marginTop: 4,
          width: '100%',
          height: '55px',
          alignSelf: 'center',
          marginBottom: 2,
        }}>
        Start my transformation!
      </Button>
      <Typography
        sx={{
          fontSize: '20px',
          fontWeight: 400,
          color: colors.OFF_BLACK,
          margin: '20px 0px',
        }}>
        Balanced nutrition to support all aspects of your wellbeing
      </Typography>
      <Box
        sx={{
          padding: '25px 36px',
          borderRadius: '20px',
          background: colors.CREAM,
          display: 'flex',
          flexDirection: 'column',
          width: '280px',
          margin: '20px auto',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', margin: '5px 0px' }}>
          <img src={GoalsImg} height={40} alt="icon" />
          <Typography
            sx={{ fontSize: '14px', fontWeight: 500, marginLeft: '20px' }}>
            Supports weight goals
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', margin: '5px 0px' }}>
          <img src={BoostImg} height={40} alt="icon" />
          <Typography
            sx={{ fontSize: '14px', fontWeight: 500, marginLeft: '20px' }}>
            Boosts mood and energy
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', margin: '5px 0px' }}>
          <img src={SleepImg} height={40} alt="icon" />
          <Typography
            sx={{ fontSize: '14px', fontWeight: 500, marginLeft: '20px' }}>
            Improves sleep quality
          </Typography>
        </Box>
      </Box>
      <Typography
        sx={{
          fontSize: '20px',
          fontWeight: 400,
          color: colors.OFF_BLACK,
          margin: '20px 0px',
        }}>
        Expert advice at your fingertips
      </Typography>
      <Typography
        sx={{
          fontSize: '18px',
          fontWeight: 400,
          color: colors.OFF_BLACK,
          marginTop: '20px',
          padding: '20px',
          borderRadius: '20px',
          border: `1px solid ${colors.SALAD}33`,
          backgroundColor: colors.OFF_WHITE,
        }}>
        We’ve helped thousands of people hit their transformation goals!
      </Typography>
      <Button
        variant="contained"
        onClick={handleNavigate}
        size="large"
        sx={{
          textTransform: 'none',
          borderRadius: '100px',
          marginTop: 4,
          width: '100%',
          height: '55px',
          alignSelf: 'center',
          marginBottom: 2,
        }}>
        Start my transformation!
      </Button>
      <ClientTestimonials />
      <Button
        variant="contained"
        onClick={handleNavigate}
        size="large"
        sx={{
          textTransform: 'none',
          borderRadius: '100px',
          marginTop: 4,
          width: '100%',
          height: '55px',
          alignSelf: 'center',
          marginBottom: 2,
        }}>
        Start my transformation!
      </Button>
    </Box>
  );
};
export default PlanSummary;
